import React, { createContext, useContext, useMemo } from "react";
import {
  EditingTeamRole,
  type GetModernizedCourseEditingTeamQuery,
  useGetModernizedCourseEditingTeamQuery,
} from "@axio/graphql/generated/graphql";
import { useAuth } from "../../../../../hooks/useAuth";

export type TeamRole = "architect" | "faculty" | "instructional-designer" | "observer";
type EditingTeamResult = GetModernizedCourseEditingTeamQuery["getModernizedCourseEditingTeam"];

export interface TeamMember {
  id: string;
  name: string;
  role: TeamRole;
  email?: string;
}

const ROLE_MAP: Record<EditingTeamRole, TeamRole> = {
  [EditingTeamRole.Architect]: "architect",
  [EditingTeamRole.Instructor]: "faculty",
  [EditingTeamRole.InstructionalDesigner]: "instructional-designer",
  [EditingTeamRole.Observer]: "observer",
};

interface TeamMembersContextValue {
  teamMembers: TeamMember[];
  currentUserId: string;
  currentUserName: string;
}

const TeamMembersContext = createContext<TeamMembersContextValue>({
  teamMembers: [],
  currentUserId: "",
  currentUserName: "",
});

export function useTeamMembers(): TeamMember[] {
  return useContext(TeamMembersContext).teamMembers;
}

export function useCurrentUserId(): string {
  return useContext(TeamMembersContext).currentUserId;
}

export function useCurrentUserName(): string {
  return useContext(TeamMembersContext).currentUserName;
}

export function TeamMembersProvider({
  courseId,
  editingTeamResult,
  children,
}: {
  courseId: string;
  editingTeamResult?: EditingTeamResult | null;
  children: React.ReactNode;
}) {
  const { userInfo } = useAuth();
  const hasPreloadedEditingTeamResult = editingTeamResult !== undefined;
  const { data } = useGetModernizedCourseEditingTeamQuery({
    variables: { courseId },
    fetchPolicy: "cache-and-network",
    skip: hasPreloadedEditingTeamResult,
  });
  const resolvedEditingTeamResult = hasPreloadedEditingTeamResult
    ? editingTeamResult
    : data?.getModernizedCourseEditingTeam;

  const teamMembers = useMemo<TeamMember[]>(() => {
    const members = resolvedEditingTeamResult?.members;
    if (!members) return [];
    const mappedMembers = members.map((m) => ({
      id: m.userId,
      name: m.name,
      role: ROLE_MAP[m.role],
      email: m.email ?? undefined,
    }));

    const architectId = resolvedEditingTeamResult?.architectId;
    if (architectId && architectId === userInfo.userId && !mappedMembers.some((member) => member.id === architectId)) {
      mappedMembers.push({
        id: userInfo.userId,
        name: userInfo.userName,
        role: "architect",
      });
    }

    return mappedMembers;
  }, [resolvedEditingTeamResult, userInfo.userId, userInfo.userName]);

  const value = useMemo<TeamMembersContextValue>(
    () => ({
      teamMembers,
      currentUserId: userInfo.userId,
      currentUserName: userInfo.userName,
    }),
    [teamMembers, userInfo.userId, userInfo.userName]
  );

  return <TeamMembersContext.Provider value={value}>{children}</TeamMembersContext.Provider>;
}
